import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PropertyCard from '../components/PropertyCard';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { propertiesApi, districtsApi } from '../services/api';
import {
  ArrowLeft,
  MapPin,
  Bed,
  Bath,
  Maximize,
  Car,
  Calendar,
  Building,
  Phone,
  Mail,
  Share2,
  Heart,
  Check,
  Building2,
  Wifi,
  MessageSquare,
  MessageCircle
} from 'lucide-react';

const PropertyDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, getLocalizedText } = useLanguage();
  
  const [property, setProperty] = useState(null);
  const [district, setDistrict] = useState(null);
  const [similarProperties, setSimilarProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const propertyData = await propertiesApi.getById(id);
        setProperty(propertyData);
        
        // Fetch district
        if (propertyData.district_id) {
          const districtData = await districtsApi.getById(propertyData.district_id);
          setDistrict(districtData);
        }
        
        // Fetch similar properties
        const allProperties = await propertiesApi.getAll();
        const similar = allProperties
          .filter(p => (p.property_id || p.id) !== id && 
            (p.type === propertyData.type || p.district_id === propertyData.district_id))
          .slice(0, 3);
        setSimilarProperties(similar);
        
      } catch (error) {
        console.error('Error fetching property:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="pt-24 pb-16 flex justify-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="pt-24 pb-16 text-center">
          <h1 className="text-2xl font-bold text-gray-900">Property not found</h1>
          <Link to="/properties">
            <Button className="mt-4">Back to Properties</Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const formatPrice = (price, priceType) => {
    // If price is already a string with currency symbol, return as-is
    if (typeof price === 'string' && (price.includes('$') || price.includes('€') || price.includes('UZS') || price.includes('₩'))) {
      return priceType === 'rent' ? `${price}${t('perMonth')}` : price;
    }
    
    // Otherwise format as number
    const formatted = Number(price).toLocaleString();
    return priceType === 'rent' ? `$${formatted}${t('perMonth')}` : `$${formatted}`;
  };

  const getBadgeClass = (badge) => {
    switch (badge) {
      case 'premium': return 'bg-gradient-to-r from-amber-500 to-yellow-500 text-white';
      case 'hot': return 'bg-gradient-to-r from-red-500 to-orange-500 text-white';
      case 'new': return 'bg-gradient-to-r from-green-500 to-emerald-500 text-white';
      default: return '';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50">
      <Header />
      
      <main className="pt-20 pb-16">
        {/* Back Button */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('backToMap')}
          </Button>
        </div>

        {/* Image Gallery */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-96 md:h-[500px]">
            <div className="relative rounded-2xl overflow-hidden">
              <img
                src={property.images?.[0] || '/placeholder-property.jpg'}
                alt={getLocalizedText(property.title)}
                className="w-full h-full object-cover"
              />
              {property.badge && (
                <span className={`absolute top-4 left-4 px-4 py-2 rounded-full text-sm font-semibold ${getBadgeClass(property.badge)}`}>
                  {t(property.badge)}
                </span>
              )}
            </div>
            <div className="hidden md:grid grid-cols-2 gap-4">
              {property.images?.slice(1, 5).map((img, idx) => (
                <div key={idx} className="relative rounded-2xl overflow-hidden">
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
              {(!property.images || property.images.length < 5) && (
                <div className="rounded-2xl bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                  <span className="text-blue-600 font-medium">More photos coming</span>
                </div>
              )}
            </div>
          </div>
        </div>


        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Header */}
              <div>
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                    {property.price_type === 'rent' ? t('forRent') : t('forSale')}
                  </span>
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                    {property.type === 'apartment' ? t('apartments') : property.type === 'house' ? t('houses') : t('commercial')}
                  </span>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                  {getLocalizedText(property.title)}
                </h1>
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin className="w-5 h-5 text-blue-500" />
                  <span>{getLocalizedText(property.address)}, {getLocalizedText(district?.name)}</span>
                </div>
              </div>

              {/* Price */}
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <p className="text-4xl font-bold text-blue-600">
                  {formatPrice(property.price, property.price_type)}
                </p>
              </div>

              {/* Features Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {property.bedrooms > 0 && (
                  <Card className="border-0 shadow-md">
                    <CardContent className="p-4 text-center">
                      <Bed className="w-6 h-6 mx-auto mb-2 text-blue-500" />
                      <p className="text-2xl font-bold text-gray-900">{property.bedrooms}</p>
                      <p className="text-sm text-gray-500">{t('bedrooms')}</p>
                    </CardContent>
                  </Card>
                )}
                <Card className="border-0 shadow-md">
                  <CardContent className="p-4 text-center">
                    <Bath className="w-6 h-6 mx-auto mb-2 text-blue-500" />
                    <p className="text-2xl font-bold text-gray-900">{property.bathrooms}</p>
                    <p className="text-sm text-gray-500">{t('bathroom')}</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-md">
                  <CardContent className="p-4 text-center">
                    <Maximize className="w-6 h-6 mx-auto mb-2 text-blue-500" />
                    <p className="text-2xl font-bold text-gray-900">{property.area}</p>
                    <p className="text-sm text-gray-500">{t('sqm')}</p>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-md">
                  <CardContent className="p-4 text-center">
                    <Building className="w-6 h-6 mx-auto mb-2 text-blue-500" />
                    <p className="text-2xl font-bold text-gray-900">{property.floor}/{property.total_floors}</p>
                    <p className="text-sm text-gray-500">{t('floor')}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Description */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4">{t('description')}</h2>
                <p className="text-gray-600 leading-relaxed">
                  {getLocalizedText(property.description)}
                </p>
              </div>

              {/* Amenities */}
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4">{t('features')}</h2>
                <div className="flex flex-wrap gap-3">
                  {property.features?.map((feature, idx) => (
                    <span
                      key={idx}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-full text-sm font-medium"
                    >
                      <Check className="w-4 h-4" />
                      {feature}
                    </span>
                  ))}
                  {property.parking && (
                    <span className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-full text-sm font-medium">
                      <Car className="w-4 h-4" />
                      {t('parking')}
                    </span>
                  )}
                </div>
              </div>

              {/* Year Built */}
              <div className="flex items-center gap-3 text-gray-600">
                <Calendar className="w-5 h-5 text-blue-500" />
                <span>Built in {property.year}</span>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Contact Card */}
              <Card className="border-0 shadow-xl sticky top-24">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">{t('contactAgent')}</h3>
                  
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-xl font-bold text-blue-600">UZ</span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Zamira</p>
                      <p className="text-sm text-gray-500">Verified Agent</p>
                    </div>
                  </div>

                  <div className="space-y-3 mb-6">
                    <a
                      href="tel:+998998187365"
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                    >
                      <Phone className="w-5 h-5 text-blue-600" />
                      <span className="text-gray-700">+998 99 818 73 65</span>
                    </a>
                    <a
                      href="mailto:zami1967@mail.ru"
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                    >
                      <Mail className="w-5 h-5 text-blue-600" />
                      <span className="text-gray-700">zami1967@mail.ru</span>
                    </a>
                    <a
                      href="https://t.me/Zamirel"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                    >
                      <MessageSquare className="w-5 h-5 text-blue-600" />
                      <span className="text-gray-700">Telegram: @Zamirel</span>
                    </a>
                    <a
                      href="https://wa.me/998998187365"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                    >
                      <MessageCircle className="w-5 h-5 text-green-600" />
                      <span className="text-gray-700">WhatsApp: +998 99 818 73 65</span>
                    </a>
                  </div>

                  <Button className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-xl mb-3">
                    {t('scheduleViewing')}
                  </Button>
                  
                  <div className="flex gap-3">
                    <Button variant="outline" className="flex-1 h-10 rounded-xl gap-2">
                      <Heart className="w-4 h-4" />
                      Save
                    </Button>
                    <Button variant="outline" className="flex-1 h-10 rounded-xl gap-2">
                      <Share2 className="w-4 h-4" />
                      Share
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Similar Properties */}
          {similarProperties.length > 0 && (
            <div className="mt-16">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('similarProperties')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {similarProperties.map((prop) => (
                  <PropertyCard key={prop.property_id || prop.id} property={prop} />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PropertyDetailPage;
